<template>
<div>
          <!-- Section 1: Select Source Data -->
          <div class="mb-4">
            <h6 class="font-weight-bold mb-3">1. Select Source Data</h6>
            <div class="row">
              <div class="col-md-4 mb-3">
                <div class="card h-100" :class="{ 'border-primary': sourceData === 'timeTracking' }">
                  <div class="card-body">
                    <div class="custom-control custom-radio">
                      <input 
                        type="radio" 
                        id="timeTracking" 
                        name="sourceData" 
                        class="custom-control-input" 
                        value="timeTracking" 
                        v-model="sourceData"
                      >
                      <label class="custom-control-label w-100" for="timeTracking">
                        <div class="d-flex align-items-start">
                          <div class="source-icon time-tracking-icon mr-2">
                            <ClockIcon size="20" />
                          </div>
                          <div>
                            <div class="font-weight-bold">Time Tracking</div>
                            <small class="text-muted">Transfers tracked labour hours</small>
                          </div>
                        </div>
                      </label>
                    </div>
                  </div>
                </div>
              </div>
              
              <div class="col-md-4 mb-3">
                <div class="card h-100" :class="{ 'border-primary': sourceData === 'purchaseOrders' }">
                  <div class="card-body">
                    <div class="custom-control custom-radio">
                      <input 
                        type="radio" 
                        id="purchaseOrders" 
                        name="sourceData" 
                        class="custom-control-input" 
                        value="purchaseOrders" 
                        v-model="sourceData"
                      >
                      <label class="custom-control-label w-100" for="purchaseOrders">
                        <div class="d-flex align-items-start">
                          <div class="source-icon purchase-orders-icon mr-2">
                            <PackageIcon size="20" />
                          </div>
                          <div>
                            <div class="font-weight-bold">Purchase Orders</div>
                            <small class="text-muted">Transfers materials/services from POs</small>
                          </div>
                        </div>
                      </label>
                    </div>
                  </div>
                </div>
              </div>
              
              <div class="col-md-4 mb-3">
                <div class="card h-100" :class="{ 'border-primary': sourceData === 'tasks' }">
                  <div class="card-body">
                    <div class="custom-control custom-radio">
                      <input 
                        type="radio" 
                        id="tasks" 
                        name="sourceData" 
                        class="custom-control-input" 
                        value="tasks" 
                        v-model="sourceData"
                      >
                      <label class="custom-control-label w-100" for="tasks">
                        <div class="d-flex align-items-start">
                          <div class="source-icon tasks-icon mr-2">
                            <CheckSquareIcon size="20" />
                          </div>
                          <div>
                            <div class="font-weight-bold">Tasks</div>
                            <small class="text-muted">Transfers task-related work time and details</small>
                          </div>
                        </div>
                      </label>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          
          <!-- Section 2: Select Transfer Method -->
          <div class="mb-4">
            <h6 class="font-weight-bold mb-3">2. Select Transfer Method</h6>
            <div class="row">
              <div class="col-md-6 mb-3">
                <div class="card h-100" :class="{ 'border-primary': transferMethod === 'sumTotal' }">
                  <div class="card-body">
                    <div class="custom-control custom-radio">
                      <input 
                        type="radio" 
                        id="sumTotal" 
                        name="transferMethod" 
                        class="custom-control-input" 
                        value="sumTotal" 
                        v-model="transferMethod"
                      >
                      <label class="custom-control-label w-100" for="sumTotal">
                        <div class="d-flex align-items-start">
                          <div class="method-icon mr-2">
                            <SumIcon size="20" />
                          </div>
                          <div>
                            <div class="font-weight-bold">Sum Total Hours as Labour Charges</div>
                            <small class="text-muted">Aggregates total time and adds it as a single line item</small>
                          </div>
                        </div>
                      </label>
                    </div>
                  </div>
                </div>
              </div>
              
              <div class="col-md-6 mb-3">
                <div class="card h-100" :class="{ 'border-primary': transferMethod === 'groupByType' }">
                  <div class="card-body">
                    <div class="custom-control custom-radio">
                      <input 
                        type="radio" 
                        id="groupByType" 
                        name="transferMethod" 
                        class="custom-control-input" 
                        value="groupByType" 
                        v-model="transferMethod"
                      >
                      <label class="custom-control-label w-100" for="groupByType">
                        <div class="d-flex align-items-start">
                          <div class="method-icon mr-2">
                            <LayersIcon size="20" />
                          </div>
                          <div>
                            <div class="font-weight-bold">Group by Type & Sum Time Tracking Hours</div>
                            <small class="text-muted">Groups time logs into categories (Regular, Travel, Overtime, etc.)</small>
                          </div>
                        </div>
                      </label>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          
          <!-- Section 3: Additional Options -->
          <div class="mb-4">
            <h6 class="font-weight-bold mb-3">3. Additional Options</h6>
            <div class="card">
              <div class="card-body">
                <div class="custom-control custom-checkbox mb-2">
                  <input 
                    type="checkbox" 
                    class="custom-control-input" 
                    id="transferDescription" 
                    v-model="options.transferDescription"
                  >
                  <label class="custom-control-label" for="transferDescription">
                    Transfer Description as Service Details
                  </label>
                </div>
                
                <div class="custom-control custom-checkbox mb-2">
                  <input 
                    type="checkbox" 
                    class="custom-control-input" 
                    id="transferNotes" 
                    v-model="options.transferNotes"
                  >
                  <label class="custom-control-label" for="transferNotes">
                    Transfer Description as Notes
                  </label>
                </div>
                
                <div class="custom-control custom-checkbox mb-2">
                  <input 
                    type="checkbox" 
                    class="custom-control-input" 
                    id="includeTechnician" 
                    v-model="options.includeTechnician"
                  >
                  <label class="custom-control-label" for="includeTechnician">
                    Include Technician Name or Task Assignee
                  </label>
                </div>
                
                <div class="custom-control custom-checkbox mb-2">
                  <input 
                    type="checkbox" 
                    class="custom-control-input" 
                    id="applyPORates" 
                    v-model="options.applyPORates"
                  >
                  <label class="custom-control-label" for="applyPORates">
                    Apply Purchase Order Rates (if applicable)
                  </label>
                </div>
                
                <div class="custom-control custom-checkbox mb-2">
                  <input 
                    type="checkbox" 
                    class="custom-control-input" 
                    id="allowCustomRate" 
                    v-model="options.allowCustomRate"
                  >
                  <label class="custom-control-label" for="allowCustomRate">
                    Allow Custom Rate Override
                  </label>
                </div>
                
                <div v-if="options.allowCustomRate" class="form-group mt-3 ml-4">
                  <label for="customRate">Custom Rate ($)</label>
                  <div class="input-group" style="max-width: 200px;">
                    <div class="input-group-prepend">
                      <span class="input-group-text">$</span>
                    </div>
                    <input 
                      type="number" 
                      class="form-control" 
                      id="customRate" 
                      v-model="customRate" 
                      min="0" 
                      step="0.01" 
                      placeholder="0.00"
                    >
                  </div>
                </div>
              </div>
            </div>
          </div>
          
          <!-- Preview Panel -->
          <div class="mb-4">
            <h6 class="font-weight-bold mb-3">Preview</h6>
            <div class="card bg-light">
              <div class="card-body">
                <div v-if="sourceData && transferMethod" class="preview-data">
                  <div class="table-responsive">
                    <table class="table">
                      <thead>
                        <tr>
                          <th>Item</th>
                          <th>Quantity</th>
                          <th>Rate</th>
                          <th class="text-right">Amount</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr v-if="transferMethod === 'sumTotal'">
                          <td>{{ getSourceLabel() }} - Labour</td>
                          <td>{{ mockData.hours }} hrs</td>
                          <td>${{ customRate || mockData.rate }}/hr</td>
                          <td class="text-right">${{ calculateTotal() }}</td>
                        </tr>
                        <template v-else-if="transferMethod === 'groupByType'">
                          <tr v-for="(item, index) in mockData.categories" :key="index">
                            <td>{{ item.name }}</td>
                            <td>{{ item.hours }} hrs</td>
                            <td>${{ customRate || item.rate }}/hr</td>
                            <td class="text-right">${{ calculateItemTotal(item) }}</td>
                          </tr>
                        </template>
                      </tbody>
                      <tfoot>
                        <tr>
                          <th colspan="3">Total</th>
                          <th class="text-right">${{ calculateGrandTotal() }}</th>
                        </tr>
                      </tfoot>
                    </table>
                  </div>
                </div>
                <div v-else class="text-center py-4">
                  <FileTextIcon size="48" class="text-muted mb-3" />
                  <p class="text-muted">Select source data and transfer method to see preview</p>
                </div>
              </div>
            </div>
          </div>      
        
        
  </div>
</template>

<script>
import XIcon from 'vue-feather-icons/icons/XIcon'
import ClockIcon from 'vue-feather-icons/icons/ClockIcon'
import PackageIcon from 'vue-feather-icons/icons/PackageIcon'
import CheckSquareIcon from 'vue-feather-icons/icons/CheckSquareIcon'
import LayersIcon from 'vue-feather-icons/icons/LayersIcon'
import FileIcon from 'vue-feather-icons/icons/FileIcon'
import EVENTS from "@/constants/events";
import MODULE from "@/constants/modules";
import GlobalServices from "@/services/global";
 import editMixin from "@/mixin/edit-mixin";



let global = new GlobalServices();

// Custom Sum icon since it's not in vue-feather-icons
const SumIcon = {
  template: `
    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
      <path d="M18 16H6l6-6"></path>
      <path d="M18 8H6"></path>
    </svg>
  `
};

export default {
   mixins: [editMixin],
  components: {
    ClockIcon,
    PackageIcon,
    CheckSquareIcon,
    LayersIcon,
    FileTextIcon: FileIcon,
    SumIcon
  },
  data() {
    return {
      sourceData: '',
      transferMethod: '',
      options: {
        transferDescription: true,
        transferNotes: false,
        includeTechnician: true,
        applyPORates: false,
        allowCustomRate: false
      },
      customRate: null,
      // Mock data for preview
      mockData: {
        hours: 8.5,
        rate: 75,
        categories: [
          { name: 'Regular Hours', hours: 6, rate: 75 },
          { name: 'Travel Time', hours: 1.5, rate: 60 },
          { name: 'Overtime', hours: 1, rate: 112.5 }
        ]
      }
    };
  },
  methods: {
    closeModal() {
      // Emit close event to parent component
      // This would be implemented by the parent
      //console.log('Modal closed');
      this.$emit('close');
    },
    confirmTransfer() {
      // Emit confirm event with selected data to parent component
      const result = {
        sourceData: this.sourceData,
        transferMethod: this.transferMethod,
        options: this.options,
        customRate: this.customRate
      };
      //console.log('Transfer confirmed:', result);
      this.$emit('confirm', result);
      this.closeModal();
    },
    getSourceLabel() {
      switch (this.sourceData) {
        case 'timeTracking': return 'Time Tracking';
        case 'purchaseOrders': return 'Purchase Orders';
        case 'tasks': return 'Tasks';
        default: return '';
      }
    },
    async generatePreview() {
      //Get list of time Tracking

      const param = {
        //Dynamically passs any additional paramaters required for api calls etc.
        id: this.modalObj.data.id,
        parentActionId: MODULE.PROJECTS.ActionId, //Parent Table the actionId table must be filtered on, by Id
        actionId: MODULE.TIMETRACKING.ActionId, //Action Id to determine what table list data to return
      };
      // Call the API to get the data

       const response = await global.getDataByActionId(param, true);

      // This method would generate a preview based on the selected options
      // For now, we are using mock data

      return this.mockData;
    },
    calculateTotal() {
      const rate = this.customRate || this.mockData.rate;
      return (this.mockData.hours * rate).toFixed(2);
    },
    calculateItemTotal(item) {
      const rate = this.customRate || item.rate;
      return (item.hours * rate).toFixed(2);
    },
    calculateGrandTotal() {
      if (this.transferMethod === 'sumTotal') {
        return this.calculateTotal();
      } else {
        let total = 0;
        this.mockData.categories.forEach(item => {
          total += parseFloat(this.calculateItemTotal(item));
        });
        return total.toFixed(2);
      }
    },
    mounted() {
      //console.log("Testing id", this.modalObj.data.id);
    },
  }
};
</script>

<style scoped>
/* Bootstrap 4 Modal Backdrop */
.modal-backdrop {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1050;
}

.modal-dialog {
  margin: 1.75rem auto;
  max-width: 800px;
  width: 90%;
}

/* Custom styling for source icons */
.source-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border-radius: 4px;
}

.time-tracking-icon {
  color: #007bff;
}

.purchase-orders-icon {
  color: #28a745;
}

.tasks-icon {
  color: #fd7e14;
}

.method-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  color: #6c757d;
}

/* Make cards clickable */
.card {
  transition: all 0.2s;
  cursor: pointer;
}

.card:hover {
  box-shadow: 0 0.125rem 0.25rem rgba(0, 0, 0, 0.075);
}

.border-primary {
  border-color: #007bff !important;
  box-shadow: 0 0 0 0.2rem rgba(0, 123, 255, 0.25);
}

/* Custom control label width fix */
.custom-control-label.w-100::before,
.custom-control-label.w-100::after {
  top: 0.25rem;
}

/* Fix for custom checkboxes */
.custom-control {
  padding-left: 1.5rem;
}
</style>